/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * Distribution API hooks for managing distribution groups and contacts
 * Calls Python API endpoints at ses-distribution-api
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import { useCallback, useEffect, useState } from "react";

// Get API base URL from environment (same as main API)
const DISTRIBUTION_API_URL =
  process.env.REACT_APP_API_ENDPOINT?.replace(/\/graphql$/, "") || "";

// =====================================
// Distribution API Types
// =====================================

/** ユーザーサマリー */
export interface UserSummary {
  id: string;
  name: string;
  email: string;
}

/** グループサマリー */
export interface GroupSummary {
  id: string;
  name: string;
  isDefault: boolean;
}

/** グループ内の連絡先 */
export interface ContactInGroup {
  id: string;
  companyName: string;
  contactName: string | null;
  email: string;
  isActive: boolean;
}

/** グループ一覧アイテム */
export interface DistributionGroup {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  contactCount: number;
  createdBy: UserSummary;
  createdAt: string;
}

/** グループ詳細 */
export interface DistributionGroupDetail {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  senderId: string;
  createdBy: UserSummary;
  createdAt: string;
  updatedAt: string;
  contacts: ContactInGroup[];
}

/** グループ一覧レスポンス */
export interface GroupListResponse {
  items: DistributionGroup[];
  total: number;
  page: number;
  limit: number;
}

/** グループ作成リクエスト */
export interface CreateGroupInput {
  name: string;
  description?: string;
  isDefault?: boolean;
}

/** グループ更新リクエスト */
export interface UpdateGroupInput {
  name?: string;
  description?: string;
  isDefault?: boolean;
}

/** 連絡先一覧アイテム */
export interface DistributionContact {
  id: string;
  companyName: string;
  contactName: string | null;
  email: string;
  isActive: boolean;
  groups: GroupSummary[];
  createdAt: string;
}

/** 配信履歴アイテム */
export interface DeliveryHistoryItem {
  id: string;
  subject: string;
  status: string;
  sentAt: string | null;
}

/** 連絡先詳細 */
export interface DistributionContactDetail {
  id: string;
  companyName: string;
  contactName: string | null;
  email: string;
  isActive: boolean;
  notes: string | null;
  managedBy: UserSummary | null;
  groups: GroupSummary[];
  deliveryHistory: DeliveryHistoryItem[];
  createdAt: string;
  updatedAt: string;
}

/** 連絡先一覧レスポンス */
export interface ContactListResponse {
  items: DistributionContact[];
  total: number;
  page: number;
  limit: number;
}

/** 連絡先作成リクエスト */
export interface CreateContactInput {
  companyName: string;
  contactName?: string;
  email: string;
  notes?: string;
  isActive?: boolean;
  groupIds?: string[];
}

/** 連絡先更新リクエスト */
export interface UpdateContactInput {
  companyName?: string;
  contactName?: string;
  email?: string;
  notes?: string;
  isActive?: boolean;
  groupIds?: string[];
}

/** CSVインポート結果 */
export interface ImportResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; error: string }>;
}

// =====================================
// Generic fetch helper
// =====================================

/**
 * 認証トークンを取得する
 * @return {Promise<string | null>} 認証トークン
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch (_e) {
    return null;
  }
}

/**
 * Distribution API呼び出しヘルパー
 * @param {string} method HTTPメソッド
 * @param {string} path APIパス
 * @param {unknown} body リクエストボディ
 * @return {Promise<T>} レスポンス
 */
async function fetchDistributionApi<T>(
  method: "GET" | "POST" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = await getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const options: RequestInit = {
    method,
    headers,
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const res = await fetch(`${DISTRIBUTION_API_URL}${path}`, options);

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      (errorBody as { detail?: string }).detail ||
        `API error: ${res.status} ${res.statusText}`,
    );
  }

  if (res.status === 204) {
    return {} as T;
  }

  return res.json();
}

/**
 * FormDataを使用してDistribution APIを呼び出すヘルパー
 * @param {string} path APIパス
 * @param {FormData} formData フォームデータ
 * @return {Promise<T>} レスポンス
 */
async function fetchDistributionApiFormData<T>(
  path: string,
  formData: FormData,
): Promise<T> {
  const headers: Record<string, string> = {};

  const token = await getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${DISTRIBUTION_API_URL}${path}`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    throw new Error(
      (errorBody as { detail?: string }).detail ||
        `API error: ${res.status} ${res.statusText}`,
    );
  }

  return res.json();
}

// =====================================
// Group API Hooks
// =====================================

export interface UseGroupsApiOptions {
  page?: number;
  limit?: number;
  search?: string;
}

export interface UseGroupsApiResult {
  data: GroupListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<GroupListResponse>;
}

/**
 * グループ一覧を取得するフック
 * @param {UseGroupsApiOptions} options オプション
 * @return {UseGroupsApiResult} グループ一覧データとローディング状態
 */
export function useGroupsApi(
  options: UseGroupsApiOptions = {},
): UseGroupsApiResult {
  const [data, setData] = useState<GroupListResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchGroups = useCallback(async (): Promise<GroupListResponse> => {
    setLoading(true);
    setError(undefined);

    try {
      const params = new URLSearchParams();
      if (options.page) params.append("page", String(options.page));
      if (options.limit) params.append("limit", String(options.limit));
      if (options.search) params.append("search", options.search);

      const queryString = params.toString();
      const path = `/api/groups${queryString ? `?${queryString}` : ""}`;

      const responseData = await fetchDistributionApi<GroupListResponse>(
        "GET",
        path,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.page, options.limit, options.search]);

  useEffect(() => {
    fetchGroups().catch(() => {});
  }, [fetchGroups]);

  return { data, error, loading, refetch: fetchGroups };
}

export interface UseGroupApiOptions {
  id: string;
  skip?: boolean;
}

export interface UseGroupApiResult {
  data: DistributionGroupDetail | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<DistributionGroupDetail>;
}

/**
 * グループ詳細を取得するフック
 * @param {UseGroupApiOptions} options オプション
 * @return {UseGroupApiResult} グループ詳細データとローディング状態
 */
export function useGroupApi(options: UseGroupApiOptions): UseGroupApiResult {
  const [data, setData] = useState<DistributionGroupDetail | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchGroup = useCallback(async (): Promise<DistributionGroupDetail> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await fetchDistributionApi<DistributionGroupDetail>(
        "GET",
        `/api/groups/${options.id}`,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchGroup().catch(() => {});
    }
  }, [options.skip, options.id, fetchGroup]);

  return { data, error, loading, refetch: fetchGroup };
}

export interface UseCreateGroupApiResult {
  createGroup: (input: CreateGroupInput) => Promise<DistributionGroupDetail>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * グループを作成するフック
 * @return {UseCreateGroupApiResult} 作成関数とローディング状態
 */
export function useCreateGroupApi(): UseCreateGroupApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createGroup = useCallback(
    async (input: CreateGroupInput): Promise<DistributionGroupDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData =
          await fetchDistributionApi<DistributionGroupDetail>(
            "POST",
            "/api/groups",
            input,
          );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createGroup, loading, error };
}

export interface UseUpdateGroupApiResult {
  updateGroup: (
    id: string,
    input: UpdateGroupInput,
  ) => Promise<DistributionGroupDetail>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * グループを更新するフック
 * @return {UseUpdateGroupApiResult} 更新関数とローディング状態
 */
export function useUpdateGroupApi(): UseUpdateGroupApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateGroup = useCallback(
    async (
      id: string,
      input: UpdateGroupInput,
    ): Promise<DistributionGroupDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData =
          await fetchDistributionApi<DistributionGroupDetail>(
            "PUT",
            `/api/groups/${id}`,
            input,
          );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateGroup, loading, error };
}

export interface UseDeleteGroupApiResult {
  deleteGroup: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * グループを削除するフック
 * @return {UseDeleteGroupApiResult} 削除関数とローディング状態
 */
export function useDeleteGroupApi(): UseDeleteGroupApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteGroup = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchDistributionApi<void>("DELETE", `/api/groups/${id}`);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteGroup, loading, error };
}

export interface UseAddContactsToGroupApiResult {
  addContacts: (
    groupId: string,
    contactIds: string[],
  ) => Promise<{ added: number }>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * グループに連絡先を追加するフック
 * @return {UseAddContactsToGroupApiResult} 追加関数とローディング状態
 */
export function useAddContactsToGroupApi(): UseAddContactsToGroupApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const addContacts = useCallback(
    async (
      groupId: string,
      contactIds: string[],
    ): Promise<{ added: number }> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await fetchDistributionApi<{ added: number }>(
          "POST",
          `/api/groups/${groupId}/contacts`,
          { contactIds },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { addContacts, loading, error };
}

export interface UseRemoveContactFromGroupApiResult {
  removeContact: (groupId: string, contactId: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * グループから連絡先を削除するフック
 * @return {UseRemoveContactFromGroupApiResult} 削除関数とローディング状態
 */
export function useRemoveContactFromGroupApi(): UseRemoveContactFromGroupApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const removeContact = useCallback(
    async (groupId: string, contactId: string): Promise<void> => {
      setLoading(true);
      setError(undefined);

      try {
        await fetchDistributionApi<void>(
          "DELETE",
          `/api/groups/${groupId}/contacts/${contactId}`,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { removeContact, loading, error };
}

// =====================================
// Contact API Hooks
// =====================================

export interface UseContactsApiOptions {
  page?: number;
  limit?: number;
  search?: string;
  groupId?: string;
  isActive?: boolean;
}

export interface UseContactsApiResult {
  data: ContactListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<ContactListResponse>;
}

/**
 * 連絡先一覧を取得するフック
 * @param {UseContactsApiOptions} options オプション
 * @return {UseContactsApiResult} 連絡先一覧データとローディング状態
 */
export function useContactsApi(
  options: UseContactsApiOptions = {},
): UseContactsApiResult {
  const [data, setData] = useState<ContactListResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchContacts = useCallback(async (): Promise<ContactListResponse> => {
    setLoading(true);
    setError(undefined);

    try {
      const params = new URLSearchParams();
      if (options.page) params.append("page", String(options.page));
      if (options.limit) params.append("limit", String(options.limit));
      if (options.search) params.append("search", options.search);
      if (options.groupId) params.append("groupId", options.groupId);
      if (options.isActive !== undefined)
        params.append("isActive", String(options.isActive));

      const queryString = params.toString();
      const path = `/api/contacts${queryString ? `?${queryString}` : ""}`;

      const responseData = await fetchDistributionApi<ContactListResponse>(
        "GET",
        path,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [
    options.page,
    options.limit,
    options.search,
    options.groupId,
    options.isActive,
  ]);

  useEffect(() => {
    fetchContacts().catch(() => {});
  }, [fetchContacts]);

  return { data, error, loading, refetch: fetchContacts };
}

export interface UseContactApiOptions {
  id: string;
  skip?: boolean;
}

export interface UseContactApiResult {
  data: DistributionContactDetail | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<DistributionContactDetail>;
}

/**
 * 連絡先詳細を取得するフック
 * @param {UseContactApiOptions} options オプション
 * @return {UseContactApiResult} 連絡先詳細データとローディング状態
 */
export function useContactApi(
  options: UseContactApiOptions,
): UseContactApiResult {
  const [data, setData] = useState<DistributionContactDetail | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchContact =
    useCallback(async (): Promise<DistributionContactDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData =
          await fetchDistributionApi<DistributionContactDetail>(
            "GET",
            `/api/contacts/${options.id}`,
          );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchContact().catch(() => {});
    }
  }, [options.skip, options.id, fetchContact]);

  return { data, error, loading, refetch: fetchContact };
}

export interface UseCreateContactApiResult {
  createContact: (
    input: CreateContactInput,
  ) => Promise<DistributionContactDetail>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 連絡先を作成するフック
 * @return {UseCreateContactApiResult} 作成関数とローディング状態
 */
export function useCreateContactApi(): UseCreateContactApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createContact = useCallback(
    async (input: CreateContactInput): Promise<DistributionContactDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData =
          await fetchDistributionApi<DistributionContactDetail>(
            "POST",
            "/api/contacts",
            input,
          );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createContact, loading, error };
}

export interface UseUpdateContactApiResult {
  updateContact: (
    id: string,
    input: UpdateContactInput,
  ) => Promise<DistributionContactDetail>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 連絡先を更新するフック
 * @return {UseUpdateContactApiResult} 更新関数とローディング状態
 */
export function useUpdateContactApi(): UseUpdateContactApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateContact = useCallback(
    async (
      id: string,
      input: UpdateContactInput,
    ): Promise<DistributionContactDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData =
          await fetchDistributionApi<DistributionContactDetail>(
            "PUT",
            `/api/contacts/${id}`,
            input,
          );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateContact, loading, error };
}

export interface UseDeleteContactApiResult {
  deleteContact: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * 連絡先を削除するフック
 * @return {UseDeleteContactApiResult} 削除関数とローディング状態
 */
export function useDeleteContactApi(): UseDeleteContactApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteContact = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await fetchDistributionApi<void>("DELETE", `/api/contacts/${id}`);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteContact, loading, error };
}

export interface UseImportContactsApiResult {
  importContacts: (file: File, groupId?: string) => Promise<ImportResult>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * CSVファイルから連絡先をインポートするフック
 * @return {UseImportContactsApiResult} インポート関数とローディング状態
 */
export function useImportContactsApi(): UseImportContactsApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const importContacts = useCallback(
    async (file: File, groupId?: string): Promise<ImportResult> => {
      setLoading(true);
      setError(undefined);

      try {
        const formData = new FormData();
        formData.append("file", file);
        if (groupId) {
          formData.append("group_id", groupId);
        }

        const responseData = await fetchDistributionApiFormData<ImportResult>(
          "/api/contacts/import",
          formData,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { importContacts, loading, error };
}

// =====================================
// Delete All Contacts
// =====================================

export interface UseDeleteAllContactsApiResult {
  deleteAllContacts: () => Promise<{ deleted: number }>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook for deleting all contacts.
 * @return {UseDeleteAllContactsApiResult} deleteAllContacts function, loading state, and error
 */
export function useDeleteAllContactsApi(): UseDeleteAllContactsApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteAllContacts = useCallback(async (): Promise<{
    deleted: number;
  }> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await fetchDistributionApi<{ deleted: number }>(
        "DELETE",
        "/api/contacts",
      );
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteAllContacts, loading, error };
}

// =====================================
// Export Contacts CSV
// =====================================

export interface UseExportContactsApiResult {
  exportContacts: () => Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * Hook for exporting contacts as CSV.
 * @return {UseExportContactsApiResult} exportContacts function, loading state, and error
 */
export function useExportContactsApi(): UseExportContactsApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const exportContacts = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      const token = await getAuthToken();

      const response = await fetch(
        `${DISTRIBUTION_API_URL}/api/contacts/export`,
        {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contacts_export.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { exportContacts, loading, error };
}
